/**
 * useChat — top-level state hook. Multi-thread aware (main room + per-PM
 * tabs). Holds the WS connection, authenticated `me`, per-thread
 * messages, unread counts, and an active-tab pointer.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { connect, type WSClient, type WSState } from '../lib/ws.js';
import { parse } from '../lib/slashCommands.js';
import { play as playSound } from '../lib/soundboard.js';
import { loadPrefs, savePref, emitChange } from '../lib/prefs.js';
import type {
    ChatMessage, ServerFrame, UserListEntry, UserView,
} from '../lib/types.js';
import { K } from '../lib/types.js';

// ---- Connect 4 slice ------------------------------------------------------
export interface C4Pending {
    kind: 'invite_sent' | 'invite_received';
    game_id: string;
    other: UserView;            // opponent (the one who isn't us)
    deadline: number;           // unix ms
}
export interface C4Active {
    game_id: string;
    players: [string, string];
    /** 0|1 if you're a player in this game; null if you're spectating. */
    you_play: 0 | 1 | null;
    turn: 0 | 1;
    board: number[][];
    status: 'playing' | 'ended';
    reason: 'win' | 'draw' | 'resigned' | null;
    winner: 0 | 1 | null;
    /** Pinned room (for spectator routing). Empty/undefined for legacy games. */
    room?: string;
}

const ENTRANCE = 'entrance';
const MAX_MESSAGES = 500;

/** True for unauthenticated/guest sockets. Each guest is anonymous so
 *  there's nothing meaningful to colorize for them. */
function isGuestUser(u: { id?: number; name?: string; k?: number }): boolean {
    return u.k === K.Anon || u.id === 0 || u.name === 'Guest';
}

/** The standard rooms the legacy SWF surfaced as bottom tabs (visible
 *  in m0de / musicdemon / SephyxCore / TrixiePuff screenshots). Order
 *  matches those captures. "This Profile" is added at runtime from
 *  ?u=<username>; Veterans/Subscribers are rank-gated server-side. */
export const STANDARD_ROOMS = [
    'entrance', 'multiplayer', 'connect4', 'yahtzee',
    'veterans', 'subscribers', 'garbagebin',
] as const;

/** Read the legacy `?u=<username>` URL param so we know whose profile we're
 *  on (the "This Profile" tab maps to `profile/<username>`). The slash form
 *  is the server's canonical separator — accepted by joinChat's RoomName
 *  regex and used throughout rooms.ts / auth.ts. */
export function profileRoomName(): string | null {
    try {
        const params = new URLSearchParams(window.location.search);
        const u = params.get('u');
        return u && /^[A-Za-z0-9 _-]{1,32}$/.test(u) ? `profile/${u}` : null;
    } catch { return null; }
}

export type TabId =
    | { kind: 'room'; name: string }
    | { kind: 'pm';   other: string };

export function tabKey(t: TabId): string {
    return t.kind === 'room' ? `room:${t.name}` : `pm:${t.other}`;
}

type ChatAction =
    | { t: 'session'; me: UserView }
    | { t: 'state';   s: WSState }
    | { t: 'msg';     key: string; m: ChatMessage }
    | { t: 'users';   room: string; users: UserListEntry[] }
    | { t: 'userJoin';  room: string; user: UserListEntry }
    | { t: 'userLeave'; room: string; uid: number }
    | { t: 'forgetRoom'; room: string }
    /** Re-saturate any messages in any thread that name a particular user
     *  with their current rank/shop colour. Fires when a user joins so
     *  their backlog messages stop showing the stale gray fallback. */
    | { t: 'recolorMessages'; name: string; color: string }
    | { t: 'userStatus'; room: string; uname: string; s: number }
    | { t: 'userStatusAll'; room: string; s: number }
    | { t: 'closed';  room: string; closed: boolean; until: number | null }
    | { t: 'muted';   minutes: number; until: number | null }
    | { t: 'banned';  until: number | null; reason?: string }
    | { t: 'nowPlaying'; title: string; artist: string; url?: string }
    | { t: 'ignore';   user: string; on: boolean }
    | { t: 'openPm';   other: string }
    | { t: 'closePm';  other: string }
    | { t: 'setActive'; tab: TabId }
    | { t: 'clearActive' }
    | { t: 'c4_pending'; p: C4Pending | null }
    | { t: 'c4_active';  a: C4Active  | null }
    | { t: 'ping'; ms: number }
    | { t: 'topic'; room: string; topic: string; set_by: string; set_at: number }
    | { t: 'history'; room: string; messages: ChatMessage[] };

interface ChatState {
    me: UserView | null;
    wsState: WSState;
    /** Per-thread messages, keyed by tabKey(). */
    threads: Record<string, ChatMessage[]>;
    /** Unread count per thread; 0 (or missing) = no badge. */
    unread: Record<string, number>;
    active: TabId;
    /** Open PM tabs in order. */
    pmList: string[];
    users: Record<string, UserListEntry[]>;
    closed: Record<string, { closed: boolean; until: number | null }>;
    mutedUntil: number | null;
    banned: { until: number | null; reason?: string } | null;
    ignore: Set<string>;
    nowPlaying: { title: string; artist: string; url?: string } | null;
    c4Pending: C4Pending | null;
    c4Active:  C4Active  | null;
    /** Round-trip latency in ms (rolling). null until first probe completes. */
    pingMs: number | null;
    /** Per-room pinned topic (set by a mod). Empty string = no topic. */
    topics: Record<string, { topic: string; set_by: string; set_at: number }>;
    /** Per-room "history loaded?" flag so we only fetch once per session. */
    historyLoaded: Record<string, boolean>;
}

const INITIAL_ACTIVE: TabId = { kind: 'room', name: ENTRANCE };
const INITIAL: ChatState = {
    me: null, wsState: 'connecting',
    threads: {}, unread: {}, active: INITIAL_ACTIVE, pmList: [],
    users: {}, closed: {}, mutedUntil: null, banned: null,
    // Hydrate ignore list from persisted prefs. Survives reloads.
    ignore: new Set(loadPrefs().ignoreList),
    nowPlaying: null,
    c4Pending: null, c4Active: null,
    pingMs: null,
    topics: {},
    historyLoaded: {},
};

function pushTo(threads: Record<string, ChatMessage[]>, key: string, m: ChatMessage):
    Record<string, ChatMessage[]> {
    const cur = threads[key] ?? [];
    const next = [...cur, m];
    if (next.length > MAX_MESSAGES) next.splice(0, next.length - MAX_MESSAGES);
    return { ...threads, [key]: next };
}

function reduce(s: ChatState, a: ChatAction): ChatState {
    switch (a.t) {
        case 'session': return { ...s, me: a.me };
        case 'state':   return { ...s, wsState: a.s };
        case 'msg': {
            if (a.m.from && s.ignore.has(a.m.from.name)) return s;
            const threads = pushTo(s.threads, a.key, a.m);
            const active = tabKey(s.active);
            const unread = active === a.key ? s.unread : { ...s.unread, [a.key]: (s.unread[a.key] ?? 0) + 1 };
            return { ...s, threads, unread };
        }
        case 'users':    return { ...s, users: { ...s.users, [a.room]: a.users } };
        case 'recolorMessages': {
            // Walk every thread; for each message whose `from.name` matches
            // the joining user, update its `from.color`. O(threads × msgs)
            // but threads cap at ~10 and msgs at MAX_MESSAGES=500 → fine.
            let touched = false;
            const nextThreads: Record<string, ChatMessage[]> = {};
            for (const [k, msgs] of Object.entries(s.threads)) {
                let dirty = false;
                const next = msgs.map(m => {
                    if (m.from && m.from.name === a.name && m.from.color !== a.color) {
                        dirty = true;
                        return { ...m, from: { ...m.from, color: a.color } };
                    }
                    return m;
                });
                if (dirty) { nextThreads[k] = next; touched = true; }
                else        { nextThreads[k] = msgs; }
            }
            return touched ? { ...s, threads: nextThreads } : s;
        }
        case 'forgetRoom': {
            // Drop our local view of the room — used when we explicitly
            // leave so the room-tab user-count stops including us.
            const { [a.room]: _u, ...usersRest } = s.users;
            const { [a.room]: _c, ...closedRest } = s.closed;
            const { [a.room]: _t, ...topicsRest } = s.topics;
            const { [a.room]: _h, ...histRest } = s.historyLoaded;
            void _u; void _c; void _t; void _h;
            return {
                ...s,
                users: usersRest,
                closed: closedRest,
                topics: topicsRest,
                historyLoaded: histRest,
            };
        }
        case 'userJoin': {
            const cur = s.users[a.room] ?? [];
            if (cur.some(u => u.id === a.user.id)) return s;
            return { ...s, users: { ...s.users, [a.room]: [...cur, a.user] } };
        }
        case 'userLeave': {
            const cur = s.users[a.room]; if (!cur) return s;
            return { ...s, users: { ...s.users, [a.room]: cur.filter(u => u.id !== a.uid) } };
        }
        case 'userStatus': {
            const cur = s.users[a.room]; if (!cur) return s;
            const next = cur.map(u => u.name === a.uname ? { ...u, uStatus: a.s } : u);
            return { ...s, users: { ...s.users, [a.room]: next } };
        }
        case 'userStatusAll': {
            const cur = s.users[a.room]; if (!cur) return s;
            const next = cur.map(u => ({ ...u, uStatus: a.s }));
            return { ...s, users: { ...s.users, [a.room]: next } };
        }
        case 'closed':  return { ...s, closed: { ...s.closed, [a.room]: { closed: a.closed, until: a.until } } };
        case 'muted':   return { ...s, mutedUntil: a.until };
        case 'banned':  return { ...s, banned: { until: a.until, reason: a.reason } };
        case 'nowPlaying': return { ...s, nowPlaying: { title: a.title, artist: a.artist, url: a.url } };
        case 'ignore': {
            const ig = new Set(s.ignore);
            if (a.on) ig.add(a.user); else ig.delete(a.user);
            // Persist outside the reducer (side effect tolerated for prefs).
            savePref('ignoreList', Array.from(ig));
            emitChange();
            return { ...s, ignore: ig };
        }
        case 'openPm': {
            if (s.pmList.includes(a.other)) return s;
            return { ...s, pmList: [...s.pmList, a.other] };
        }
        case 'closePm': {
            const pmList = s.pmList.filter(x => x !== a.other);
            const key = `pm:${a.other}`;
            const { [key]: _msgs, ...threadsRest } = s.threads;
            const { [key]: _u, ...unreadRest } = s.unread;
            void _msgs; void _u;
            const active: TabId = (s.active.kind === 'pm' && s.active.other === a.other)
                ? INITIAL_ACTIVE
                : s.active;
            return { ...s, pmList, threads: threadsRest, unread: unreadRest, active };
        }
        case 'setActive': {
            const key = tabKey(a.tab);
            const { [key]: _u, ...rest } = s.unread;
            void _u;
            return { ...s, active: a.tab, unread: rest };
        }
        case 'clearActive': {
            const key = tabKey(s.active);
            const threads = { ...s.threads, [key]: [] };
            return { ...s, threads };
        }
        case 'c4_pending': return { ...s, c4Pending: a.p };
        case 'c4_active':  return { ...s, c4Active:  a.a };
        case 'ping':       return { ...s, pingMs: a.ms };
        case 'topic':      return { ...s, topics: { ...s.topics, [a.room]: { topic: a.topic, set_by: a.set_by, set_at: a.set_at } } };
        case 'history': {
            // Prepend history (oldest-first) BEFORE the live messages already
            // collected in this thread. De-dupe by `t` so a live frame that
            // arrived between fetch and apply isn't doubled.
            const key = `room:${a.room}`;
            const live = s.threads[key] ?? [];
            const liveTs = new Set(live.map(m => m.t + ':' + (m.from?.name ?? '')));
            const hist = a.messages.filter(m => !liveTs.has(m.t + ':' + (m.from?.name ?? '')));
            const merged = [...hist, ...live];
            return {
                ...s,
                threads: { ...s.threads, [key]: merged },
                historyLoaded: { ...s.historyLoaded, [a.room]: true },
            };
        }
        default: return s;
    }
}

let localIdCounter = 0;
const localId = () => ++localIdCounter;
const nowSec = () => Math.floor(Date.now() / 1000);

interface UseChatReturn {
    state: ChatState;
    submit: (input: string) => void;
    /** Send a raw server frame directly (used by the mod menu). */
    sendRaw: (frame: object) => void;
    openPm: (user: string) => void;
    setActive: (tab: TabId) => void;
    closePm: (other: string) => void;
    /** The currently-active room (follows the active tab when it's a room). */
    room: string;
    /** Switch active room — joins it server-side if not already joined. */
    switchRoom: (name: string) => void;
    clear: () => void;
    lastPmFrom: string | null;
    /** Connect 4 actions */
    c4Invite:  (to: string) => void;
    c4Accept:  () => void;
    c4Decline: () => void;
    c4Move:    (col: number) => void;
    c4Resign:  () => void;
    c4Dismiss: () => void;
    c4Rematch: () => void;
    c4Stats:   (user?: string) => void;
}

export interface UseChatOpts {
    /** Fires when a /bomb /walrus /bucket event arrives so the parent can
        spawn the matching sprite overlay. Optional — drop-in compatible. */
    onEasterEgg?: (kind: 'bomb' | 'walrus' | 'bucket') => void;
    /** Gate sound playback (sound_message + incoming-c4-invite). When
        false, frames still arrive and render, just no audio. Lets the
        Options panel's "Play Sounds" pref work. */
    canPlaySound?: () => boolean;
    /** Fires when a PM arrives while NOT viewing that PM tab — lets the
        parent flash the tab or show a desktop notification. */
    onPmAlert?: (other: string) => void;
    /** Fires when a screen-wide chat_cmd Easter egg toggles — the parent
        applies/removes a CSS filter on the chat root. Legacy SWF effects
        from frame_22 (Invert_Color / Rotate_Chat). */
    onScreenFx?: (kind: 'invert' | 'rotate') => void;
}

export function useChat(opts: UseChatOpts = {}): UseChatReturn {
    const [state, dispatch] = useReducer(reduce, INITIAL);
    const wsRef = useRef<WSClient | null>(null);
    const lastPmFromRef = useRef<string | null>(null);
    const stateRef = useRef<ChatState>(state);
    stateRef.current = state;
    const optsRef = useRef(opts);
    optsRef.current = opts;
    // The "active room" follows the active tab when it's a room tab.
    // Falls back to the entrance when the active tab is a PM.
    const room = state.active.kind === 'room' ? state.active.name : ENTRANCE;
    // Track which rooms we've already sent join_chat for so we don't duplicate.
    const joinedRoomsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        const ws = connect();
        wsRef.current = ws;
        ws.onstate(s => dispatch({ t: 'state', s }));
        ws.onmessage(raw => handleFrame(raw as ServerFrame));
        return () => ws.close();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-(re)join: fires whenever we transition into a fresh session
    // (state.me identity changes) OR the active room changes. On any drop,
    // ws.ts reconnects, the server sends a new session frame, state.me
    // updates, and this effect re-runs — re-issuing get_chat + join_chat
    // so the user lands back in their room without a manual refresh.
    useEffect(() => {
        const ws = wsRef.current;
        if (!ws || !state.me) return;
        // Reset joined-set on session change (server forgets between sessions).
        if (joinedRoomsRef.current.size === 0 || !joinedRoomsRef.current.has(room)) {
            ws.send({ cmd: 'get_chat', room_name: room });
            ws.send({ cmd: 'join_chat', room });
            joinedRoomsRef.current.add(room);
            // Fetch persisted history (last 50) once per room per session.
            // Server side joinChat already pushes the chat_topic on join.
            if (!stateRef.current.historyLoaded[room]) {
                fetch(`/prochat/api/history?room=${encodeURIComponent(room)}&limit=50`)
                    .then(r => r.ok ? r.json() : null)
                    .then((j: { messages?: Array<{ kind: string; from?: { id: number; name: string }; body: string; t: number; sound_p?: string }> } | null) => {
                        if (!j?.messages?.length) {
                            dispatch({ t: 'history', room, messages: [] });
                            return;
                        }
                        const cur = stateRef.current.users[room] ?? [];
                        const lookupColor = (name?: string): string => {
                            if (!name) return '#888';
                            const u = cur.find(x => x.name === name);
                            return u?.color ?? '#888';
                        };
                        const msgs: ChatMessage[] = j.messages.map(m => ({
                            localId: localId(),
                            kind: m.kind,
                            room,
                            from: m.from ? { id: m.from.id, name: m.from.name, k: 16, color: lookupColor(m.from.name), is_mod: false, is_admin: false } : undefined,
                            body: m.body,
                            sound_p: m.sound_p,
                            t: m.t,
                        }));
                        dispatch({ t: 'history', room, messages: msgs });
                    })
                    .catch(() => { /* graceful: just no history */ });
            }
        }
    }, [state.me, room]);

    // When the session resets (new state.me identity), clear the joined-set.
    useEffect(() => {
        joinedRoomsRef.current = new Set();
    }, [state.me?.id]);

    // Periodic latency probe: send client_ping every 10s once authenticated;
    // the matching res:client_ping reply is handled in handleFrame.
    const pendingPingsRef = useRef<Map<string, number>>(new Map());
    useEffect(() => {
        if (!state.me) return;
        const ws = wsRef.current;
        if (!ws) return;
        const tick = () => {
            const ws2 = wsRef.current;
            if (!ws2) return;
            const id = Math.random().toString(36).slice(2, 10);
            pendingPingsRef.current.set(id, Date.now());
            try { ws2.send({ cmd: 'client_ping', req_id: id }); } catch { /* ignore */ }
            // Drop stale pending pings (>30s) so the map doesn't grow.
            const now = Date.now();
            for (const [k, t] of pendingPingsRef.current) {
                if (now - t > 30_000) pendingPingsRef.current.delete(k);
            }
        };
        const interval = setInterval(tick, 10_000);
        // First probe ~500ms after auth so the WS settles.
        const first = setTimeout(tick, 500);
        return () => { clearInterval(interval); clearTimeout(first); };
    }, [state.me]);

    // When the socket goes from 'open' to 'closed'/'error', surface a
    // friendly system line so the user knows the chat is reconnecting.
    const lastWsStateRef = useRef<WSState>('connecting');
    useEffect(() => {
        const prev = lastWsStateRef.current;
        lastWsStateRef.current = state.wsState;
        if (prev === 'open' && (state.wsState === 'closed' || state.wsState === 'error')) {
            dispatch({ t: 'msg', key: `room:${room}`, m: {
                localId: localId(), kind: 'system', room,
                body: 'Connection lost — reconnecting…', t: nowSec(),
            }});
        } else if (prev !== 'open' && state.wsState === 'open' && prev !== 'connecting') {
            dispatch({ t: 'msg', key: `room:${room}`, m: {
                localId: localId(), kind: 'system', room,
                body: 'Reconnected.', t: nowSec(),
            }});
        }
    }, [state.wsState, room]);

    const sysMsg = useCallback((body: string, key?: string) => {
        const k = key ?? tabKey(stateRef.current.active);
        dispatch({ t: 'msg', key: k, m: { localId: localId(), kind: 'system', room, body, t: nowSec() } });
    }, [room]);

    function handleFrame(f: ServerFrame) {
        const roomKey = `room:${room}`;
        if ('err' in f) {
            if (f.err === 'banned') dispatch({ t: 'banned', until: f.until ?? null, reason: f.reason });
            if (f.err === 'muted')  dispatch({ t: 'muted',  minutes: 0, until: f.until ?? null });
            const k = tabKey(stateRef.current.active);
            dispatch({ t: 'msg', key: k, m: { localId: localId(), kind: 'error', room, body: `[${f.err}]${f.detail ? ' ' + f.detail : ''}`, t: nowSec() } });
            return;
        }
        if ('res' in f) {
            // client_ping round-trip — compute latency
            if (f.res === 'client_ping' && (f as { req_id?: string }).req_id) {
                const id = (f as { req_id: string }).req_id;
                const sent = pendingPingsRef.current.get(id);
                if (sent !== undefined) {
                    pendingPingsRef.current.delete(id);
                    dispatch({ t: 'ping', ms: Date.now() - sent });
                }
            }
            if (f.res === 'chat_topic') {
                dispatch({ t: 'topic', room: f.room, topic: f.topic, set_by: f.set_by, set_at: f.set_at });
            }
            return;
        }
        switch (f.evt) {
            case 'session': {
                const isReconnect = !!stateRef.current.me && stateRef.current.me.id === f.me.id;
                dispatch({ t: 'session', me: f.me });
                if (isReconnect) {
                    sysMsg(`Reconnected as ${f.me.name}.`, roomKey);
                } else {
                    sysMsg(`Welcome, ${f.me.name}!  Type /help for commands or click any user to view their profile.`, roomKey);
                }
                break;
            }
            case 'user_join': {
                dispatch({ t: 'userJoin', room: f.room, user: f.user });
                // Re-saturate any backlog messages bearing this user's name
                // (history-replay falls back to gray `#888` for names not in
                // the room at load time; when they rejoin, recolor).
                if (!isGuestUser(f.user)) {
                    dispatch({ t: 'recolorMessages', name: f.user.name, color: f.user.color });
                }
                // Join/leave are intentionally NOT rendered as chat-log lines —
                // they cluttered the local transcript and pushed real
                // conversation out of view. Presence still updates the userlist
                // (dispatch above), so the sidebar reflects who's in the room.
                break;
            }
            case 'user_leave': {
                // Userlist updates so the sidebar reflects the departure; we no
                // longer emit a "left" chat-log line (see user_join above).
                dispatch({ t: 'userLeave', room: f.room, uid: f.uid });
                break;
            }
            case 'userlist': {
                dispatch({ t: 'users', room: f.room, users: f.users });
                // Re-saturate backlog: every user in the room has a known
                // color now; any message bearing one of those names should
                // pick it up. Skip guests.
                for (const u of f.users) {
                    if (!isGuestUser(u)) {
                        dispatch({ t: 'recolorMessages', name: u.name, color: u.color });
                    }
                }
                break;
            }
            case 'chat_state':
            case 'chat_opened':
            case 'chat_closed': {
                const closed = f.evt === 'chat_closed' || (f.evt === 'chat_state' && (f as { closed: boolean }).closed);
                const until = (f as { until?: number | null }).until ?? null;
                dispatch({ t: 'closed', room: f.room, closed, until });
                if (f.evt !== 'chat_state') {
                    dispatch({ t: 'msg', key: `room:${f.room}`, m: { localId: localId(), kind: 'system', room: f.room,
                        body: f.evt === 'chat_closed'
                            ? `Room closed by ${f.by ?? 'a mod'}${'minutes' in f && f.minutes ? ` for ${f.minutes}m` : ''}`
                            : `Room opened by ${f.by ?? 'a mod'}`,
                        t: f.t }});
                }
                break;
            }
            case 'say':
            case 'generic_mess':
            case 'user_mess':
            case 'blank_mess':
            case 'chat_cmd': {
                dispatch({ t: 'msg', key: `room:${f.room}`, m: {
                    localId: localId(), kind: f.evt, room: f.room,
                    from: (f as { from?: UserView }).from,
                    to_uid: (f as { to_uid?: number }).to_uid,
                    body: ('m' in f ? (f.m as string) : '') || ('c' in f ? `/${(f as { c: string }).c}` : ''),
                    t: f.t,
                }});
                // Faithful screen-wide Easter eggs from the legacy SWF:
                // `Invert_Color` toggles a stage-wide blend-mode filter,
                // `Rotate_Chat` toggles a 180° rotation. Toggle behavior
                // mirrors the SWF (if normal → enable, else → disable).
                if (f.evt === 'chat_cmd' && 'c' in f) {
                    const c = (f as { c: string }).c;
                    if (c === 'Invert_Color') optsRef.current.onScreenFx?.('invert');
                    else if (c === 'Rotate_Chat') optsRef.current.onScreenFx?.('rotate');
                }
                break;
            }
            case 'pm': {
                const me = stateRef.current.me;
                const other = me && f.from.id === me.id ? f.to : f.from.name;
                const key = `pm:${other}`;
                const isInbound = !!(me && f.from.id !== me.id);
                const activeTab = stateRef.current.active;
                const isActivePm = activeTab.kind === 'pm' && activeTab.other === other;
                if (!stateRef.current.pmList.includes(other)) dispatch({ t: 'openPm', other });
                dispatch({ t: 'msg', key, m: {
                    localId: localId(), kind: 'pm', room: f.room,
                    from: f.from, to_uid: undefined,
                    body: f.m, t: f.t,
                }});
                if (isInbound) lastPmFromRef.current = f.from.name;
                // PM notification: chime + visual flash, but only if we're not
                // already looking at that PM tab.
                if (isInbound && !isActivePm) {
                    if (optsRef.current.canPlaySound?.() ?? true) {
                        playSound('arnold/hey');     // arnold "hey" is the legacy PM-alert clip
                    }
                    optsRef.current.onPmAlert?.(other);
                }
                break;
            }
            case 'sound_message': {
                dispatch({ t: 'msg', key: `room:${f.room}`, m: {
                    localId: localId(), kind: 'sound_message', room: f.room,
                    from: f.from, body: f.m || `[${f.p}]`,
                    sound_p: f.p, sound_file: f.file,
                    t: f.t,
                }});
                if (optsRef.current.canPlaySound?.() ?? true) {
                    playSound(f.p, f.file);
                }
                break;
            }
            case 'ping_user':
                dispatch({ t: 'msg', key: tabKey(stateRef.current.active), m: { localId: localId(), kind: 'mod-buzz', room, body: `🔔 ${f.u} buzzed you`, t: f.t } });
                break;
            case 'slap_user':
                dispatch({ t: 'msg', key: tabKey(stateRef.current.active), m: { localId: localId(), kind: 'mod-slap', room, body: `✋ ${f.u} slapped you`, t: f.t } });
                break;
            case 'admin_ban':
                dispatch({ t: 'msg', key: `room:${f.room}`, m: { localId: localId(), kind: 'mod-ban', room: f.room, body: `🚫 ${f.uname} was banned by ${f.by}${f.minutes ? ` for ${f.minutes} min` : ''}`, t: f.t } });
                break;
            case 'admin_mute':
                dispatch({ t: 'msg', key: `room:${f.room}`, m: { localId: localId(), kind: 'mod-mute', room: f.room, body: `🔇 ${f.uname} was muted by ${f.by}${f.minutes ? ` for ${f.minutes} min` : ''}`, t: f.t } });
                break;
            case 'muted':         dispatch({ t: 'muted', minutes: f.minutes, until: f.until }); break;
            case 'user_status': {
                dispatch({ t: 'userStatus', room: f.room, uname: f.u, s: f.s });
                break;
            }
            case 'user_status_all': {
                dispatch({ t: 'userStatusAll', room: f.room, s: f.s });
                break;
            }
            case 'drop_bomb':
                // Legacy template (strings-all.txt:51 area): "<b>NAME dropped a bomb!</b>"
                dispatch({ t: 'msg', key: `room:${f.room}`, m: { localId: localId(), kind: 'mod-bomb', room: f.room, body: `${f.by} dropped a bomb!`, t: f.t } });
                optsRef.current.onEasterEgg?.('bomb');
                if (optsRef.current.canPlaySound?.() ?? true) playSound('arnold/terminated');
                break;
            case 'walrus':
                // Legacy template: "<b>A wild walrus appeared!</b>"
                dispatch({ t: 'msg', key: `room:${f.room}`, m: { localId: localId(), kind: 'mod-walrus', room: f.room, body: `A wild walrus appeared!`, t: f.t } });
                optsRef.current.onEasterEgg?.('walrus');
                if (optsRef.current.canPlaySound?.() ?? true) playSound('walrus/scream');
                break;
            case 'walrus_bucket':
                dispatch({ t: 'msg', key: `room:${f.room}`, m: { localId: localId(), kind: 'mod-bucket', room: f.room, body: `${f.by} threw the bucket!`, t: f.t } });
                optsRef.current.onEasterEgg?.('bucket');
                break;
            case 'now_playing':   dispatch({ t: 'nowPlaying', title: f.title, artist: f.artist, url: f.url }); break;
            case 'chat_topic': {
                dispatch({ t: 'topic', room: f.room, topic: f.topic, set_by: f.set_by, set_at: f.set_at });
                if (f.topic) {
                    sysMsg(`Topic set by ${f.set_by}: ${f.topic}`, `room:${f.room}`);
                } else {
                    sysMsg(`Topic cleared by ${f.set_by}`, `room:${f.room}`);
                }
                break;
            }
            case 'warning':       sysMsg(`⚠ ${f.m}`); break;
            // Connect 4
            case 'c4_invite_sent': {
                dispatch({ t: 'c4_pending', p: {
                    kind: 'invite_sent', game_id: f.game_id, other: f.to,
                    deadline: Date.now() + (f.ttl_ms ?? 60_000),
                } });
                sysMsg(`Connect 4 invite sent to ${f.to.name} (waiting…)`);
                break;
            }
            case 'c4_invite': {
                dispatch({ t: 'c4_pending', p: {
                    kind: 'invite_received', game_id: f.game_id, other: f.from,
                    deadline: Date.now() + (f.ttl_ms ?? 60_000),
                } });
                sysMsg(`${f.from.name} challenged you to Connect 4`);
                if (optsRef.current.canPlaySound?.() ?? true) playSound('tass/play_connect4');
                break;
            }
            case 'c4_decline': {
                sysMsg(`Connect 4 invite ${f.reason === 'timeout' ? 'expired' : `declined by ${f.by}`}`);
                dispatch({ t: 'c4_pending', p: null });
                dispatch({ t: 'c4_active',  a: null });
                break;
            }
            case 'c4_start': {
                // If we're a player (you_play 0|1), this is OUR game — always
                // adopt. If we're a spectator (you_play null), only adopt if
                // we don't already have an active game of our own.
                const cur = stateRef.current.c4Active;
                if (f.you_play === null && cur && cur.you_play !== null && cur.status === 'playing') break;
                dispatch({ t: 'c4_pending', p: null });
                dispatch({ t: 'c4_active', a: {
                    game_id: f.game_id, players: f.players, you_play: f.you_play,
                    turn: f.turn, board: f.board, status: 'playing',
                    reason: null, winner: null, room: f.room,
                } });
                if (f.you_play === null) {
                    sysMsg(`Connect 4: ${f.players[0]} vs ${f.players[1]} — you're spectating.`);
                } else {
                    sysMsg(`Connect 4 vs ${f.players[1 - f.you_play]} — you go ${f.you_play === 0 ? 'first (red)' : 'second (yellow)'}`);
                }
                break;
            }
            case 'c4_move': {
                const cur = stateRef.current.c4Active;
                // Spectator frame for a different game than ours — ignore.
                if (f.you_play === null && cur && cur.you_play !== null) break;
                // Spectator frame but we have no game state — adopt (we just
                // walked into a room with a game already in progress; server
                // doesn't replay c4_start so the board snapshot in this frame
                // is our entry point).
                if (f.you_play === null && !cur) {
                    dispatch({ t: 'c4_active', a: {
                        game_id: f.game_id, players: ['?','?'],
                        you_play: null, turn: f.turn, board: f.board,
                        status: f.status === 'ended' ? 'ended' : 'playing',
                        reason: null, winner: null, room: f.room,
                    } });
                    if (optsRef.current.canPlaySound?.() ?? true) playSound('arnold/no_problemo');
                    break;
                }
                dispatch({ t: 'c4_active', a: {
                    game_id: f.game_id, players: cur?.players ?? ['',''],
                    you_play: cur?.you_play ?? f.you_play,
                    turn: f.turn, board: f.board,
                    status: f.status === 'ended' ? 'ended' : 'playing',
                    reason: null, winner: null, room: cur?.room ?? f.room,
                } });
                // Piece-drop chime
                if (optsRef.current.canPlaySound?.() ?? true) playSound('arnold/no_problemo');
                break;
            }
            case 'c4_end': {
                const cur = stateRef.current.c4Active;
                // Spectator frame for a different game than ours — ignore.
                if (f.you_play === null && cur && cur.you_play !== null) break;
                const playerSlot = cur?.you_play ?? f.you_play;
                dispatch({ t: 'c4_active', a: {
                    game_id: f.game_id, players: cur?.players ?? f.players ?? ['',''],
                    you_play: playerSlot, turn: 0,
                    board: f.board, status: 'ended',
                    reason: f.reason, winner: f.winner,
                    room: cur?.room ?? f.room,
                } });
                if (f.you_play === null) {
                    // Spectator end notice — quieter, no win/lose-personal tone
                    const players = cur?.players ?? f.players ?? ['?','?'];
                    let tag: string;
                    if (f.reason === 'draw') tag = `Connect 4: ${players[0]} vs ${players[1]} — draw.`;
                    else if (f.winner !== null) tag = `Connect 4: ${players[f.winner]} won ${f.reason === 'resigned' ? '(resignation)' : ''}`.trim();
                    else tag = `Connect 4: game over.`;
                    sysMsg(tag);
                    break;
                }
                const youWon = f.winner !== null && playerSlot !== null && f.winner === playerSlot;
                const tag = f.reason === 'draw' ? 'Connect 4: draw.'
                    : youWon ? 'Connect 4: you win 🏆'
                    : 'Connect 4: you lose';
                sysMsg(tag + (f.reason === 'resigned' ? ' (resignation)' : ''));
                // Win fanfare / loss sting (players only — spectators get the line above)
                if (optsRef.current.canPlaySound?.() ?? true) {
                    if (f.reason === 'draw')      playSound('arnold/hahaha');
                    else if (youWon)              playSound('arnold/terminated');
                    else                          playSound('arnold/you_idiot');
                }
                break;
            }
            case 'c4_stats_result': {
                if (f.unknown) {
                    sysMsg(`Connect 4 stats: no such user (${f.user ?? '?'}).`);
                    break;
                }
                const tag = f.total === 0
                    ? `Connect 4: ${f.user} has no recorded games yet.`
                    : `Connect 4: ${f.user} — ${f.wins}W ${f.losses}L ${f.draws}D · resigned ${f.resigned_for}× · won by forfeit ${f.resigned_against}× · ${f.total} games`;
                sysMsg(tag);
                break;
            }
            default: break;
        }
    }

    /** Send a parsed input line; routes through active tab semantics. */
    const submit = useCallback((input: string) => {
        const ws = wsRef.current;
        if (!ws) return;
        const me = stateRef.current.me;
        const isAdmin = !!me?.is_admin;
        const active  = stateRef.current.active;
        const p = parse(input, isAdmin);

        switch (p.kind) {
            case 'say': {
                if (active.kind === 'pm') {
                    ws.send({ cmd: 'pm', to: active.other, m: p.m });
                } else {
                    ws.send({ cmd: 'say', room: active.name, m: p.m });
                }
                break;
            }
            case 'pm': {
                if (!stateRef.current.pmList.includes(p.to)) dispatch({ t: 'openPm', other: p.to });
                dispatch({ t: 'setActive', tab: { kind: 'pm', other: p.to } });
                ws.send({ cmd: 'pm', to: p.to, m: p.m });
                break;
            }
            case 'sound': ws.send({ cmd: 'sound_mess', room, p: p.p }); break;
            case 'cmd':   ws.send({ cmd: 'chat_cmd',   room, c: p.c }); break;
            case 'buzz':  ws.send({ cmd: 'ping_user',  room, user: p.to }); break;
            case 'server':
                ws.send({ cmd: p.cmd, room });
                break;
            case 'styled': {
                // Wrap the body so the receiver's Message renderer applies the style.
                // Server treats it as opaque text; client side decides how to render.
                const wrapped = `[style=${p.style}]${p.m}[/style]`;
                if (active.kind === 'pm') ws.send({ cmd: 'pm',  to: active.other, m: wrapped });
                else                       ws.send({ cmd: 'say', room: active.name, m: wrapped });
                break;
            }
            case 'c4_invite': {
                // /play_connect4 <user> — server handles game lifecycle, we get a
                // c4_invite_sent frame back to mirror the pending state in UI.
                ws.send({ cmd: 'c4_invite', to: p.to });
                break;
            }
            case 'c4_stats': {
                // /c4stats [user] — defaults to self when user is omitted.
                ws.send(p.user ? { cmd: 'c4_stats', user: p.user } : { cmd: 'c4_stats' });
                break;
            }
            case 'client': {
                const op = p.op;
                switch (op.t) {
                    case 'help': {
                        const lines = [
                            '— FFR Profile Chat commands —',
                            'BASIC:  /pm <user> <msg>  ·  /reply <msg>  ·  /me <action>  ·  /clear',
                            'USERS:  /ignore <user>  ·  /unignore <user>  ·  /profile <user>',
                            'ROOMS:  /room <name>     (entrance, multiplayer, veterans, subscribers, garbagebin)',
                            'STYLE:  /rotate <text>  ·  /invert <text>  ·  /bbc [b][i][u][color=#x][url=…][/url]  ·  /font <#hex> <text>  ·  /body <text>',
                            'GAMES:  /play_connect4 <user>  (alias /c4)  ·  /c4stats [user]',
                            'BOARDS: /soundboards (list)  ·  /<name> (any clip name)',
                            'ADMIN:  /bomb  ·  /walrus  ·  /bucket  ·  Admin/Evil Admin/Sound Board panels in the header',
                            'OPTIONS: click "Options" top-right — timestamps, sounds, color, status.',
                        ];
                        for (const l of lines) sysMsg(l);
                        break;
                    }
                    case 'clear':  dispatch({ t: 'clearActive' }); break;
                    case 'ignore':  { dispatch({ t: 'ignore', user: op.user, on: true });  sysMsg(`now ignoring ${op.user}`);  break; }
                    case 'unignore':{ dispatch({ t: 'ignore', user: op.user, on: false }); sysMsg(`no longer ignoring ${op.user}`); break; }
                    case 'profile': {
                        const u = encodeURIComponent(op.user);
                        window.open(`/profile/${u}/`, '_blank', 'noopener');
                        break;
                    }
                    case 'me': {
                        // Legacy convention: "* username does the thing" rendered italic.
                        // We send a regular `say` with a leading marker; the receiver
                        // Message component spots the marker and italicises it.
                        const action = op.text.trim();
                        if (action) ws.send({ cmd: 'say', room, m: `/me ${action}` });
                        break;
                    }
                    case 'room':   ws.send({ cmd: 'get_chat', room_name: op.room }); ws.send({ cmd: 'join_chat', room: op.room }); break;
                    case 'reply': {
                        const t = lastPmFromRef.current;
                        if (!t) { sysMsg('no PM to reply to'); break; }
                        if (!op.text) { sysMsg('usage: /reply <message>'); break; }
                        if (!stateRef.current.pmList.includes(t)) dispatch({ t: 'openPm', other: t });
                        dispatch({ t: 'setActive', tab: { kind: 'pm', other: t } });
                        ws.send({ cmd: 'pm', to: t, m: op.text });
                        break;
                    }
                    case 'soundboards': sysMsg('Soundboards: arnold (53), tass (14), takamura (1), walrus (1). Try /hasta /terminated /takamura …'); break;
                    case 'classes': {
                        // Legacy "Class Key" listing — colored rank labels with hex.
                        sysMsg('— Class Key —');
                        sysMsg('Administrator (red)   ·   Game Manager / Moderator (orange)');
                        sysMsg('Forum Mod / Profile Mod (pink)   ·   Developer (purple)');
                        sysMsg('Event Staff (blue)   ·   Simfile Judge / Author (green)');
                        sysMsg('Music Producer (yellow)   ·   Elite Keysmasher (light green)');
                        sysMsg('Veteran   ·   User   ·   Banned (red)');
                        break;
                    }
                    case 'list': {
                        const cur = stateRef.current.users[room] ?? [];
                        sysMsg(`— ${cur.length} user${cur.length === 1 ? '' : 's'} in ${room} —`);
                        if (cur.length === 0) { sysMsg('(empty)'); break; }
                        // Group by rank class for readability
                        const sorted = [...cur].sort((a, b) => a.k - b.k || a.name.localeCompare(b.name));
                        // Print 10 names per line
                        for (let i = 0; i < sorted.length; i += 10) {
                            sysMsg(sorted.slice(i, i + 10).map(u => u.name).join(', '));
                        }
                        break;
                    }
                    case 'whois': {
                        const target = (stateRef.current.users[room] ?? []).find(
                            u => u.name.toLowerCase() === op.user.toLowerCase()
                        );
                        if (!target) { sysMsg(`No such user in ${room}: ${op.user}`); break; }
                        sysMsg(`${target.name} — k=${target.k}${target.is_admin ? ' (Admin)' : target.is_mod ? ' (Mod)' : ''} color=${target.color}`);
                        break;
                    }
                    case 'version': {
                        sysMsg('prochat-revive 10.0 (web) — React + WebSocket port of profilechat10.swf');
                        break;
                    }
                    case 'time': {
                        const now = new Date();
                        sysMsg(`Local time: ${now.toLocaleString()}  ·  Unix: ${Math.floor(now.getTime()/1000)}`);
                        break;
                    }
                    case 'login':       sysMsg('Login is managed by vBulletin — visit /vbz/login.php in another tab if your session expired.'); break;
                    case 'noop':        break;
                }
                break;
            }
            case 'unknown': ws.send({ cmd: 'chat_cmd', room, c: p.c }); break;
        }
    }, [room, sysMsg]);

    const openPm = useCallback((user: string) => {
        if (!user) return;
        const me = stateRef.current.me;
        if (me && user === me.name) return; // don't pm yourself
        if (!stateRef.current.pmList.includes(user)) dispatch({ t: 'openPm', other: user });
        dispatch({ t: 'setActive', tab: { kind: 'pm', other: user } });
    }, []);

    const setActive = useCallback((tab: TabId) => { dispatch({ t: 'setActive', tab }); }, []);
    const closePm   = useCallback((other: string) => { dispatch({ t: 'closePm', other }); }, []);
    const clear     = useCallback(() => { dispatch({ t: 'clearActive' }); }, []);

    const sendRaw = useCallback((frame: object) => { wsRef.current?.send(frame); }, []);

    /** Switch the active room. The user can only be present in one room at a
     *  time (per legacy behaviour) — so we leave whatever rooms we were in
     *  before joining the new one. The room-effect above will issue
     *  get_chat + join_chat for the new room. */
    const switchRoom = useCallback((name: string) => {
        const ws = wsRef.current;
        if (!ws) return;
        // Send leave_chat for every room we're currently joined to except the
        // one we're about to switch to. (Usually a single previous room, but
        // the loop handles edge cases — e.g. reconnect cycles where two were
        // joined in quick succession.)
        for (const prev of joinedRoomsRef.current) {
            if (prev === name) continue;
            try { ws.send({ cmd: 'leave_chat', room: prev }); } catch { /* ignore */ }
            joinedRoomsRef.current.delete(prev);
            // Drop our local view of the left room so the tab user-count
            // stops including us and history reloads if we come back.
            dispatch({ t: 'forgetRoom', room: prev });
        }
        dispatch({ t: 'setActive', tab: { kind: 'room', name } });
        // If we were spectating a game in the previous room, clear it —
        // we won't get further updates and the server doesn't replay state.
        // Player games (you_play 0|1) are pinned to their original room and
        // continue to receive per-player frames regardless of where we are.
        const cur = stateRef.current.c4Active;
        if (cur && cur.you_play === null && cur.room && cur.room !== name) {
            dispatch({ t: 'c4_active', a: null });
        }
    }, []);

    // ---- Connect 4 actions -------------------------------------------------
    const c4Invite = useCallback((to: string) => {
        const t = to.trim();
        if (!t) return;
        const me = stateRef.current.me;
        if (me && t === me.name) { sysMsg("can't challenge yourself"); return; }
        wsRef.current?.send({ cmd: 'c4_invite', to: t });
    }, [sysMsg]);

    const c4Accept = useCallback(() => {
        const pend = stateRef.current.c4Pending;
        if (!pend || pend.kind !== 'invite_received') return;
        wsRef.current?.send({ cmd: 'c4_accept', game_id: pend.game_id });
    }, []);

    const c4Decline = useCallback(() => {
        const pend = stateRef.current.c4Pending;
        if (!pend) return;
        wsRef.current?.send({ cmd: 'c4_decline', game_id: pend.game_id });
        dispatch({ t: 'c4_pending', p: null });
    }, []);

    const c4Move = useCallback((col: number) => {
        const g = stateRef.current.c4Active;
        if (!g || g.status !== 'playing') return;
        if (g.you_play === null) return;         // spectator — can't move
        if (g.turn !== g.you_play) return;       // not your turn
        if (col < 0 || col > 6) return;
        wsRef.current?.send({ cmd: 'c4_move', game_id: g.game_id, col });
    }, []);

    const c4Resign = useCallback(() => {
        const g = stateRef.current.c4Active;
        if (!g || g.status !== 'playing') return;
        if (g.you_play === null) return;         // spectator — can't resign
        wsRef.current?.send({ cmd: 'c4_resign', game_id: g.game_id });
    }, []);

    const c4Dismiss = useCallback(() => {
        // Local-only — close the ended-game banner. Pending invites must use
        // accept/decline so the server can be told.
        dispatch({ t: 'c4_active', a: null });
    }, []);

    const c4Rematch = useCallback(() => {
        // After a game ends, re-invite the same opponent. No-op for spectators
        // and for games where the opponent's username isn't known.
        const g = stateRef.current.c4Active;
        if (!g || g.status !== 'ended' || g.you_play === null) return;
        const opponent = g.players[1 - g.you_play];
        if (!opponent) return;
        // Clear the ended banner locally; the server response will repopulate
        // with the new game's c4_pending → c4_start.
        dispatch({ t: 'c4_active', a: null });
        wsRef.current?.send({ cmd: 'c4_invite', to: opponent });
    }, []);

    const c4Stats = useCallback((user?: string) => {
        const u = user?.trim();
        wsRef.current?.send(u ? { cmd: 'c4_stats', user: u } : { cmd: 'c4_stats' });
    }, []);

    return useMemo<UseChatReturn>(() => ({
        state, submit, sendRaw, openPm, setActive, closePm, room, switchRoom, clear,
        lastPmFrom: lastPmFromRef.current,
        c4Invite, c4Accept, c4Decline, c4Move, c4Resign, c4Dismiss, c4Rematch, c4Stats,
    }), [state, submit, sendRaw, openPm, setActive, closePm, room, switchRoom, clear,
         c4Invite, c4Accept, c4Decline, c4Move, c4Resign, c4Dismiss, c4Rematch, c4Stats]);
}
